import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import { authFetch, useAuth } from "../context/AuthContext";
import { fileUrl } from "../components/PhotoGallery";
import { MagnifyingGlass, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const ZODIAC = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
    "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];

const SIGN_GLYPH = {
    Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
    Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};

export default function Search() {
    const { user } = useAuth();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [filters, setFilters] = useState({
        name: "",
        signs: [],
        gender: "",
        age_min: 18,
        age_max: 99,
        distance_km: 100,
        any_distance: true,
        verified_only: false,
        sort_by: "compatibility",
    });

    if (!user?.subscription_active) {
        return (
            <>
                <Navbar />
                <main className="min-h-screen pt-28 pb-12 px-6">
                    <div className="max-w-2xl mx-auto glass-gold p-12 text-center">
                        <p className="label-tracking text-gold">Premium Feature</p>
                        <h1 className="font-serif text-5xl mt-3">Search the Cosmos</h1>
                        <p className="text-muted mt-4 leading-relaxed">
                            Premium members can search by sign, age, distance, and recency.
                            Standard members can still receive messages from Premium members.
                        </p>
                        <Link to="/subscribe" className="btn-gold-solid inline-block mt-8" data-testid="search-unlock">
                            Unlock Premium
                        </Link>
                    </div>
                </main>
            </>
        );
    }

    const toggleSign = (s) => {
        const next = filters.signs.includes(s)
            ? filters.signs.filter((x) => x !== s)
            : [...filters.signs, s];
        setFilters({ ...filters, signs: next });
    };

    const doSearch = async () => {
        setLoading(true);
        setHasSearched(true);
        try {
            const payload = {
                name: filters.name || null,
                signs: filters.signs.length ? filters.signs : null,
                gender: filters.gender || null,
                age_min: filters.age_min,
                age_max: filters.age_max,
                distance_km: filters.any_distance ? null : filters.distance_km,
                verified_only: filters.verified_only,
                sort_by: filters.sort_by,
                limit: 30,
            };
            const res = await authFetch("post", "/search", payload);
            setResults(res.data.results);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Search failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-28 pb-12 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-baseline justify-between">
                        <div>
                            <p className="label-tracking text-gold">Premium Search</p>
                            <h1 className="font-serif text-4xl sm:text-5xl mt-2">Seek your soul</h1>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
                        {/* FILTERS */}
                        <aside className="glass p-6 space-y-5 lg:col-span-1 h-fit" data-testid="search-filters">
                            <div>
                                <label className="label-tracking">Name</label>
                                <input
                                    type="text" placeholder="Search by name"
                                    value={filters.name}
                                    onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                                    className="w-full mt-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-gold outline-none text-sm"
                                    data-testid="search-name"
                                />
                            </div>
                            <div>
                                <label className="label-tracking">Sun Signs ({filters.signs.length || "All"})</label>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {ZODIAC.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => toggleSign(s)}
                                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                                filters.signs.includes(s)
                                                    ? "bg-gold/15 border-gold/40 text-gold"
                                                    : "bg-white/5 border-white/10 text-muted hover:border-gold/30"
                                            }`}
                                            data-testid={`sign-filter-${s}`}
                                        >
                                            {SIGN_GLYPH[s]} {s.substring(0, 3)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="label-tracking">Gender</label>
                                <select
                                    value={filters.gender}
                                    onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                                    className="w-full mt-2 px-3 py-2 rounded-lg bg-[#0A0A1A] border border-white/10 focus:border-gold outline-none text-sm"
                                    data-testid="search-gender"
                                >
                                    <option value="">Any</option>
                                    <option value="woman">Women</option>
                                    <option value="man">Men</option>
                                    <option value="nonbinary">Non-binary</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-tracking">Age {filters.age_min} – {filters.age_max}</label>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                    <input
                                        type="number" min="18" max="99"
                                        value={filters.age_min}
                                        onChange={(e) => setFilters({ ...filters, age_min: parseInt(e.target.value, 10) || 18 })}
                                        className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 outline-none text-sm"
                                        data-testid="search-age-min"
                                    />
                                    <input
                                        type="number" min="18" max="99"
                                        value={filters.age_max}
                                        onChange={(e) => setFilters({ ...filters, age_max: parseInt(e.target.value, 10) || 99 })}
                                        className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 outline-none text-sm"
                                        data-testid="search-age-max"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={filters.any_distance}
                                        onChange={(e) => setFilters({ ...filters, any_distance: e.target.checked })}
                                        data-testid="search-any-distance"
                                    />
                                    <span className="label-tracking">Any distance</span>
                                </label>
                                {!filters.any_distance && (
                                    <div className="mt-2">
                                        <label className="label-tracking text-xs">Within {filters.distance_km} km</label>
                                        <input
                                            type="range" min="10" max="5000" step="10"
                                            value={filters.distance_km}
                                            onChange={(e) => setFilters({ ...filters, distance_km: parseInt(e.target.value, 10) })}
                                            className="w-full mt-1 accent-[#E6C387]"
                                            data-testid="search-distance"
                                        />
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={filters.verified_only}
                                        onChange={(e) => setFilters({ ...filters, verified_only: e.target.checked })}
                                        data-testid="search-verified-only"
                                    />
                                    <span className="label-tracking flex items-center gap-1">
                                        <ShieldCheck size={12} weight="fill" className="text-gold" /> Verified only
                                    </span>
                                </label>
                            </div>
                            <div>
                                <label className="label-tracking">Sort by</label>
                                <select
                                    value={filters.sort_by}
                                    onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
                                    className="w-full mt-2 px-3 py-2 rounded-lg bg-[#0A0A1A] border border-white/10 focus:border-gold outline-none text-sm"
                                    data-testid="search-sort"
                                >
                                    <option value="compatibility">Highest compatibility</option>
                                    <option value="recent">Recently active</option>
                                    <option value="newest">Newest profiles</option>
                                    <option value="distance">Nearest first</option>
                                </select>
                            </div>
                            <button
                                onClick={doSearch}
                                disabled={loading}
                                className="btn-gold-solid w-full inline-flex items-center justify-center gap-2"
                                data-testid="search-submit"
                            >
                                <MagnifyingGlass size={16} /> {loading ? "Searching..." : "Search"}
                            </button>
                        </aside>

                        {/* RESULTS */}
                        <section className="lg:col-span-2 space-y-3" data-testid="search-results">
                            {!hasSearched && (
                                <div className="glass p-12 text-center">
                                    <p className="font-serif text-2xl">Refine, then search.</p>
                                    <p className="text-muted text-sm mt-2">Combine signs, age, distance — the cosmos awaits.</p>
                                </div>
                            )}
                            {hasSearched && !loading && results.length === 0 && (
                                <div className="glass p-12 text-center">
                                    <p className="font-serif text-2xl">No souls match — yet.</p>
                                    <p className="text-muted text-sm mt-2">Try widening your filters.</p>
                                </div>
                            )}
                            {results.map((r, i) => (
                                <motion.div
                                    key={r.user_id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: i * 0.03 }}
                                >
                                    <SearchResultCard r={r} />
                                </motion.div>
                            ))}
                        </section>
                    </div>
                </div>
            </main>
        </>
    );
}

function SearchResultCard({ r }) {
    const photo = r.photos?.[0];
    return (
        <div className="glass hover-lift p-4 flex items-center gap-4" data-testid={`result-${r.user_id}`}>
            <div className="w-20 h-20 rounded-xl bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center text-3xl text-gold flex-shrink-0">
                {photo ? (
                    <img src={fileUrl(photo.storage_path)} alt="" className="w-full h-full object-cover" />
                ) : (
                    <span className="font-serif">{SIGN_GLYPH[r.sun]}</span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <h3 className="font-serif text-xl truncate">{r.name}{r.age ? `, ${r.age}` : ""}</h3>
                    {r.verified && (
                        <ShieldCheck size={16} weight="fill" className="text-gold flex-shrink-0" title="Verified" />
                    )}
                </div>
                <p className="text-xs text-muted truncate">
                    {SIGN_GLYPH[r.sun]} {r.sun} · ☽ {r.moon} · ↑ {r.rising}
                </p>
                <p className="text-xs text-muted truncate mt-0.5">
                    {r.birth_location}{r.distance_km != null ? ` · ${r.distance_km} km` : ""}
                </p>
            </div>
            <div className="flex flex-col items-end gap-2">
                <div className="text-center">
                    <p className="font-serif text-2xl text-gold leading-none">{r.compatibility.overall}</p>
                    <p className="label-tracking text-[9px]">score</p>
                </div>
                <Link
                    to={`/u/${r.user_id}`}
                    className="text-[10px] label-tracking text-gold hover:underline"
                    data-testid={`view-result-${r.user_id}`}
                >
                    View →
                </Link>
            </div>
        </div>
    );
}
